export type NotificationChannelKey = 'inApp' | 'email' | 'sms' | 'webPush' | 'mobilePush';
export type NotificationPriority = 'action' | 'attention' | 'fyi';
export type NotificationCategory =
  | 'leads'
  | 'scheduling'
  | 'contacts'
  | 'billing'
  | 'sales'
  | 'jobs'
  | 'team'
  | 'system';
export type NotificationStatusFilter = 'all' | 'unread' | 'read' | 'archived';
export type NotificationButtonTone = 'default' | 'outline' | 'ghost';

export interface NotificationPriorityMeta {
  sectionTitle: string;
  pillLabel: string;
  sectionClass: string;
  railClass: string;
  dotClass: string;
  iconTone: string;
  pillClass: string;
  rowClass: string;
  ctaVariant: NotificationButtonTone;
}

export const NOTIFICATION_PRIORITY_ORDER: readonly NotificationPriority[] = [
  'action',
  'attention',
  'fyi',
];

export const NOTIFICATION_PRIORITY_META: Record<NotificationPriority, NotificationPriorityMeta> = {
  action: {
    sectionTitle: 'Needs action',
    pillLabel: 'Action',
    sectionClass: 'text-destructive',
    railClass: 'bg-destructive',
    dotClass: 'bg-destructive',
    iconTone: 'border-destructive-border bg-destructive-surface text-destructive',
    pillClass: 'border-destructive-border bg-destructive-surface text-destructive',
    rowClass: 'bg-destructive-surface/40',
    ctaVariant: 'default',
  },
  attention: {
    sectionTitle: 'Review',
    pillLabel: 'Review',
    sectionClass: 'text-warning',
    railClass: 'bg-warning',
    dotClass: 'bg-warning',
    iconTone: 'border-warning-border bg-warning-surface text-warning',
    pillClass: 'border-warning-border bg-warning-surface text-warning',
    rowClass: 'bg-warning-surface/40',
    ctaVariant: 'outline',
  },
  fyi: {
    sectionTitle: 'FYI',
    pillLabel: 'FYI',
    sectionClass: 'text-muted-foreground',
    railClass: 'bg-muted-foreground/50',
    dotClass: 'bg-muted-foreground',
    iconTone: 'border-border bg-muted text-muted-foreground',
    pillClass: 'border-border bg-muted/60 text-muted-foreground',
    rowClass: 'bg-card',
    ctaVariant: 'ghost',
  },
};

export interface StaffNotification {
  id: string;
  recipientId: string;
  type: string;
  priority: NotificationPriority;
  category: NotificationCategory;
  groupKey: string | null;
  groupCount: number;
  firstSeenAt: string;
  lastEventAt: string;
  title: string;
  body: string;
  entityType: string | null;
  entityId: string | null;
  actionUrl: string | null;
  actorUserId: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
  seenAt: string | null;
  readAt: string | null;
  archivedAt: string | null;
}

export interface NotificationsListResponse {
  items: StaffNotification[];
  nextCursor: string | null;
}

export interface NotificationGroupItem {
  id: string;
  title: string;
  body: string;
  entityType: string | null;
  entityId: string | null;
  actionUrl: string | null;
  actorUserId: string | null;
  meta: Record<string, unknown> | null;
  occurredAt: string;
}

export interface NotificationGroupItemsResponse {
  items: NotificationGroupItem[];
  nextCursor: string | null;
}

export interface NotificationUnreadCount {
  count: number;
}

export interface NotificationPreferenceChannels {
  inApp: boolean;
  email: boolean;
  sms: boolean;
  webPush: boolean;
  mobilePush: boolean;
}

export interface NotificationPreferenceEvent {
  eventType: string;
  label: string;
  category?: NotificationCategory;
  priority?: NotificationPriority;
  channels: NotificationPreferenceChannels;
}

export interface NotificationPreferencesResponse {
  events: NotificationPreferenceEvent[];
  summary?: {
    notificationsLast7Days: number;
    lastSavedAt: string | null;
  };
}

export interface UpdateNotificationPreferencesDto {
  preferences: Array<{
    eventType: string;
    inApp?: boolean;
    email?: boolean;
    sms?: boolean;
    webPush?: boolean;
    mobilePush?: boolean;
  }>;
}

export interface OperationalNotificationRules {
  unbilledEnabled: boolean;
  unbilledAfterDays: number;
  pastWindowEnabled: boolean;
  pastWindowMinutes: number;
  receivableAgingEnabled: boolean;
  receivableAfterDays: number;
  tomorrowExceptionsEnabled: boolean;
  tomorrowCheckTime: string;
}

export type UpdateOperationalNotificationRulesDto = Partial<OperationalNotificationRules>;

export interface CreateNotificationBroadcastDto {
  title: string;
  body: string;
  type?: string;
  entityType?: string;
  entityId?: string;
  actionUrl?: string;
  meta?: Record<string, unknown>;
}

export interface CreateNotificationBroadcastResponse {
  notificationId: string;
  recipientCount: number;
  deliveryCount: number;
}
